const BASE_URL = process.env.REACT_APP_BACKEND_URL

// ========== SSE Subscription Helper ==========
export const subscribe_task_stream = (taskId, onUpdate) => {
  const es = new EventSource(`${BASE_URL}/api/project/task/stream/${taskId}`);
  es.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      if (onUpdate) onUpdate(data);
      if (data.state === 'SUCCESS' || data.state === 'FAILURE') {
        es.close();
      }
    } catch (err) {
      es.close();
    }
  };
  es.onerror = (e) => {
    // Let browser auto-reconnect per server retry; close if desired
  };
  return () => es.close();
};

export const create_project_api = async (user_id, title, description) => {
  const response = await fetch(`${BASE_URL}/api/project/create_project`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "user_id": user_id,
      "title": title,
      "description": description,
    }),
  });
  if (response.ok) {
    return await response.json(); // { message: "User registered successfully" }
  }
  if (response.status === 400) {
    throw new Error("All fields are required");
  } else {
    throw new Error("Project creation failed. Please try again.");
  }
};


export const fetch_projects_api = async (user_id) => {
  const response = await fetch(`${BASE_URL}/api/project/projects`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "user_id": user_id,
    }),
  });
  if (response.ok) {
    return await response.json();
  }
  else if(response.status === 404){
    return {projects: []};
  } else if(response.status === 400){
    throw new Error("Fetching projects failed. Bad Request");
  } else {
    throw new Error("Fetching projects failed. Internal Server Error");
  }
};


export const upload_file_api = async (file, userId, projectId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("userId", userId);
  formData.append("projectId", projectId);

  const response = await fetch(`${BASE_URL}/api/project/uploadFile`, {
    method: "POST",
    body: formData,
  });

  if (response.ok) {
    return await response.json();
  } else {
    throw new Error("File upload failed.");
  }
};


export const generate_embeddings_api = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/generateEmbeddings`, {
    method: "POST",   
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { message, task_id, status }
  } else {
    throw new Error("Generating embeddings failed.");
  }
};

export const cosine_similarity_api = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/cosine_similarity`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { message, task_id, status }
  } else {
    throw new Error("Generating embeddings failed.");
  }
};

export const umap_generation = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/generate_umap`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { task_id }
  } else {
    throw new Error("Generating embeddings failed.");
  }
};

export const  umap_calculate_eigenvalues_service = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/generate_umap/eigenvalues`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { task_id }
  } else {
    throw new Error("Generating eigenvalues failed.");
  }
};

export const pca_generation = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/generate_pca`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { task_id }
  } else {
    throw new Error("Generating embeddings failed.");
  }
};

export const  pca_calculate_eigenvalues_service = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/generate_pca/eigenvalues`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json(); // { task_id }
  } else {
    throw new Error("Generating eigenvalues failed.");
  }
};

export const cluster_service_api = async (userId, projectId, algorithm, reducedAlgorithm, n_clusters = NaN, epsilon = NaN, minPoints = NaN) => {
  const response = await fetch(`${BASE_URL}/api/project/cluster`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
      "algorithm": algorithm,
      "reducedAlgorithm": reducedAlgorithm,
      "numClusters": n_clusters,
      "epsilon": epsilon,
      "minPoints": minPoints,
    }),
  });

  if (response.ok) {
    return await response.json();
  } else {
    // Try to get error message from response
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || "Clustering failed.";
    throw new Error(errorMessage);
  }
};

export const getGraphData_api = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/cluster/graphData`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json();
  } else {
    throw new Error("Failed to fetch graph data.");
  }
};

export const convex_hull_analysis_api = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/cluster/hullMetrics`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });

  if (response.ok) {
    return await response.json();
  } else {
    throw new Error("Convex hull analysis failed.");
  }
};

export const check_embeddings_api = async (userId, projectId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/project/checkEmbeddings?userId=${userId}&projectId=${projectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        // No embeddings found - this is expected for new projects
        return { embeddings: false, imageUrl: null };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};


export const check_umap_output_api = async (userId, projectId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/project/get_umap_output?userId=${userId}&projectId=${projectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const check_pca_output_api = async (userId, projectId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/project/get_pca_output?userId=${userId}&projectId=${projectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const check_cluster_output_api = async (userId, projectId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/project/get_cluster_output?userId=${userId}&projectId=${projectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};


export const get_uploaded_file_api = async (userId, projectId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/project/getUploadedFile?userId=${userId}&projectId=${projectId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};


export const automatic_process_api = async (userId, projectId) => {
  const response = await fetch(`${BASE_URL}/api/project/automatic_process`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },    
    body: JSON.stringify({
      "userId": userId,
      "projectId": projectId,
    }),
  });     
  if (response.ok) {
    return await response.json();
  }
  else {
    throw new Error("Automatic process failed.");
  }
};
